/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

const messages = defineMessages({
    modalTitle: {
        id: 'qnaMaker.qnaMakerAzureSearchServiceSwitchingModal.modalTitle',
        defaultMessage: 'Are you sure you want to select {resourceName}?'
    },
    switch: {
        id: 'qnaMaker.qnaMakerAzureSearchServiceSwitchingModal.switch',
        defaultMessage: 'Select resource and delete projects'
    },
    cancel: {
        id: 'qnaMaker.qnaMakerAzureSearchServiceSwitchingModal.cancel',
        defaultMessage: 'Cancel'
    },
    modalDescription: {
        id: 'qnaMaker.qnaMakerAzureSearchServiceSwitchingModal.modalDescription',
        defaultMessage:
            'If you change the Azure Search resource connected to your language resource, all existing Custom question answering projects linked to the current search will be permanently deleted. To preserve your data, please export any existing projects you wish to keep before switching resources.'
    },
    errorMessage: {
        id: 'qnaMaker.qnaMakerAzureSearchServiceSwitchingModal.errorMessage',
        defaultMessage: 'please make sure to be owner on the resource {resourceName}'
    }
});

export type ServiceSwitchingModalProps = BasicModalProps<object> & {
    servicePath: string;
};

/**
 * Represents the Azure Search Service switching modal.
 */
export const ServiceSwitchingModal = withModalMounter(
    observer((props: ServiceSwitchingModalProps) => {
        const { servicePath } = props;
        const { localize } = useLocalization();
        const { dispatcher } = useGlobalState();

        const resourceName = servicePath.split('/')[8];

        const tracker = useProgressTracker({
            onComplete: () => {
                props.onDismiss();
            }
        });

        const switchService = () =>
            dispatcher.dispatch('updateAzureCsAccountSearchResourceByPath', {
                tracker,
                ownedSearchResourceId: servicePath
            });

        return (
            <BasicModal
                {...props}
                minWidth={660}
                valid
                dialogContentProps={{ title: localize(messages.modalTitle, { resourceName }) }}
                styles={{ root: { whiteSpace: 'pre-wrap' } }}
                errorMessage={
                    tracker.isFailed &&
                    `${tracker.error?.message}\n${localize(messages.errorMessage, { resourceName: servicePath.split('/')[8] })}`
                }
                disabled={tracker.isRunning || props.disabled}
                doneButtonText={localize(messages.switch)}
                cancelButtonText={localize(messages.cancel)}
                onSubmit={switchService}>
                <Stack tokens={{ childrenGap: 12, padding: '0 0 19px 0' }}>
                    <LuisText>{localize(messages.modalDescription)}</LuisText>
                </Stack>
            </BasicModal>
        );
    })
);
